import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faUniversity } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import RadioGroup from "@ts-digital/vrc/es/components/radio-group";
import { find, isEmpty, reduce } from "lodash";
import numbro from "numbro";
import { GainsightEvent, trackEvent } from "../../../../core/common/gainsight";
import { PaymentMethodName } from "../../../../PaymentContainerClient/commons/enums";
import PaymentRequestDto from "../../../../PaymentContainerClient/dtos/PaymentRequestDto";
import getFormattedMessageWithScope from "../../../../utils/getFormattedMessageWithScope";
import { isInConstraintsRange } from "../../../commons/calculators";
import getInstallmentById from "../../../commons/getInstallmentById";
import { PrivacyCookiePolicyMenu } from "../../PrivacyCookiePolicyMenu";
import americanExpress from "./americanExpress.svg";
import mastercard from "./mastercard.svg";
import paypal from "./paypal.svg";
import sepa from "./sepa.svg";
import "./style.css";
import visa from "./visa.svg";

const fm = getFormattedMessageWithScope(
    "components.PaymentForm.PaymentMethods"
);

interface PaymentMethodsProps {
    paymentRequest: PaymentRequestDto;
    onChangePaymentMethod: Function;
    selectedPaymentMethod?: PaymentMethodName;
    selectedInstallmentIds: string[];
    isPayNow: boolean;
}

export const PaymentMethods = ({
    paymentRequest,
    onChangePaymentMethod,
    selectedPaymentMethod,
    selectedInstallmentIds,
    isPayNow,
}: PaymentMethodsProps) => {
    function changePaymentMethod(e: any) {
        trackEvent(GainsightEvent.ChangeSelectedPaymentMethod, {
            paymentMethod: e.target.value,
        });
        onChangePaymentMethod(e.target.value);
    }

    // Options are ordered. Credit/debit card should be the first one
    function getPaymentMethodsOptions() {
        const options = [];
        const selectedInstallments = selectedInstallmentIds.map(
            (selectedInstallmentId) =>
                getInstallmentById(selectedInstallmentId, paymentRequest)
        );

        const tsPayCreditCardPaymentMethod = find(
            paymentRequest.availablePaymentMethods,
            {
                name: PaymentMethodName.TsPayCreditCard,
            }
        );

        if (
            tsPayCreditCardPaymentMethod &&
            isInConstraintsRange(
                reduce(
                    selectedInstallments,
                    (result, installment) =>
                        numbro(installment.amount.amount).add(result).value(),
                    0
                ).toString(),
                tsPayCreditCardPaymentMethod.constraints
            )
        ) {
            options.push({
                label: (
                    <div>
                        {fm("paymentMethods.creditDebitCard")}
                        <div className="c-PaymentForm-PaymentMethods-payment-images">
                            <img
                                alt="visa"
                                src={visa}
                                className="c-PaymentForm-PaymentMethods-payment-images-image"
                            />
                            <img
                                alt="mastercard"
                                src={mastercard}
                                className="c-PaymentForm-PaymentMethods-payment-images-image"
                            />
                            <img
                                alt="americanExpress"
                                src={americanExpress}
                                className="c-PaymentForm-PaymentMethods-payment-images-image"
                            />
                        </div>
                    </div>
                ),
                value: PaymentMethodName.TsPayCreditCard,
            });
        }

        const tsPayPayPalPaymentMethod = find(
            paymentRequest.availablePaymentMethods,
            {
                name: PaymentMethodName.TsPayPayPal,
            }
        );

        if (
            tsPayPayPalPaymentMethod &&
            isInConstraintsRange(
                reduce(
                    selectedInstallments,
                    (result, installment) =>
                        numbro(installment.amount.amount).add(result).value(),
                    0
                ).toString(),
                tsPayPayPalPaymentMethod.constraints
            )
        ) {
            options.push({
                label: (
                    <div>
                        {fm("paymentMethods.payPal")}
                        <div className="c-PaymentForm-PaymentMethods-payment-images">
                            <img
                                alt="PayPal"
                                src={paypal}
                                className="c-PaymentForm-PaymentMethods-payment-images-image"
                            />
                        </div>
                    </div>
                ),
                value: PaymentMethodName.TsPayPayPal,
            });
        }

        const tsPayBankTransferPaymentMethod = find(
            paymentRequest.availablePaymentMethods,
            {
                name: PaymentMethodName.TsPayBankTransfer,
            }
        );

        if (
            tsPayBankTransferPaymentMethod &&
            isInConstraintsRange(
                reduce(
                    selectedInstallments,
                    (result, installment) =>
                        numbro(installment.amount.amount).add(result).value(),
                    0
                ).toString(),
                tsPayBankTransferPaymentMethod.constraints
            )
        ) {
            options.push({
                label: (
                    <div>
                        {fm("paymentMethods.bankDirectDebit")}
                        <div className="c-PaymentForm-PaymentMethods-payment-images">
                            <img alt="sepa" src={sepa} />
                        </div>
                    </div>
                ),
                value: PaymentMethodName.TsPayBankTransfer,
            });
        }

        const tsPayInstantBankTransferPaymentMethod = find(
            paymentRequest.availablePaymentMethods,
            {
                name: PaymentMethodName.TsPayInstantBankTransfer,
            }
        );

        if (
            tsPayInstantBankTransferPaymentMethod &&
            isInConstraintsRange(
                reduce(
                    selectedInstallments,
                    (result, installment) =>
                        numbro(installment.amount.amount).add(result).value(),
                    0
                ).toString(),
                tsPayInstantBankTransferPaymentMethod.constraints
            )
        ) {
            options.push({
                label: (
                    <div>
                        {fm("paymentMethods.instantBankTransfer")}
                        <div className="c-PaymentForm-PaymentMethods-payment-images">
                            <FontAwesomeIcon
                                icon={faUniversity as IconProp}
                                className="c-PaymentForm-PaymentMethods-payment-images-instantBankTransferIcon"
                            />
                            {!isPayNow && (
                                <span className="c-PaymentForm-PaymentMethods-payment-notAvailable">
                                    {fm("notAvailableWithOnDeadlinePayment")}
                                </span>
                            )}
                        </div>
                    </div>
                ),
                value: PaymentMethodName.TsPayInstantBankTransfer,
                disabled: !isPayNow,
            });
        }

        return options;
    }

    const paymentMethodOptions = getPaymentMethodsOptions();

    return (
        <div className="c-PaymentForm-PaymentMethods">
            <div className="c-PaymentForm-form-title">
                {fm("title")} <PrivacyCookiePolicyMenu />
            </div>
            <div className="c-PaymentForm-PaymentMethods-methods">
                {!isEmpty(paymentMethodOptions) ? (
                    <RadioGroup
                        type="vertical"
                        className="c-PaymentForm-PaymentMethods-radioGroup"
                        value={selectedPaymentMethod}
                        onChange={changePaymentMethod}
                        options={paymentMethodOptions}
                    />
                ) : (
                    fm("noAvailablePaymentMethod")
                )}
            </div>
        </div>
    );
};
